/*
 *  -----------------------------------------------------------------------
 *    <copyright file="control-action/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import { useMutation } from '@apollo/client/react';
import React, { useState } from 'react';
import { UseBooleanActions } from 'react-hanger/array';
import { useBoolean, useTranslation } from '../../../../hooks';
import {
    DELETE_CM_OBSERVATION_ACTION,
    GET_CM_OBSERVATIONS_AND_ACTIONS,
    RESTORE_CM_OBSERVATION_ACTION,
} from '../../../../queries/central-monitoring';
import NotificationService from '../../../../utils/notification';
import { ICMActionFieldsExtended } from '../../../pages/central-monitoring/manage-actions.interface';
import ActionDialog from '../../core/action-dialog';
import DeleteReasonDialog from '../dialogs/delete-reason-dialog';
import { ControlActionProps } from './control-action.interface';
import {
    DELETE_RISK_OBSERVATION_ACTION,
    GET_RISK_OBSERVATIONS_AND_ACTIONS,
    RESTORE_RISK_OBSERVATION_ACTION,
} from '@queries/legacy-risk-assessment';

const ControlAction = ({ studyId, children, isRAM, onUpdate }: ControlActionProps) => {
    const { t } = useTranslation();

    const [currentAction, setCurrentAction] = useState(null as ICMActionFieldsExtended);

    const deleteActionOpen = useBoolean(false);
    const restoreActionOpen = useBoolean(false);

    const [deleteCMObsActMutation] = useMutation(DELETE_CM_OBSERVATION_ACTION);
    const [restoreCMObsActMutation] = useMutation(RESTORE_CM_OBSERVATION_ACTION);
    const [deleteRiskObsActMutation] = useMutation(DELETE_RISK_OBSERVATION_ACTION);
    const [restoreRiskObsActMutation] = useMutation(RESTORE_RISK_OBSERVATION_ACTION);

    const refetchQuery = isRAM ? GET_RISK_OBSERVATIONS_AND_ACTIONS : GET_CM_OBSERVATIONS_AND_ACTIONS;
    const deleteMutation = isRAM ? deleteRiskObsActMutation : deleteCMObsActMutation;
    const restoreMutation = isRAM ? restoreRiskObsActMutation : restoreCMObsActMutation;

    const handleDialogOpen = (dialog: UseBooleanActions, item: ICMActionFieldsExtended) => {
        setCurrentAction(item);
        dialog.setTrue();
    };

    const handleDialogClose = (dialog: UseBooleanActions) => dialog.setFalse();

    const handleDeleteAct = (item: ICMActionFieldsExtended) => handleDialogOpen(deleteActionOpen, item);

    const handleRestoreAct = (item: ICMActionFieldsExtended) => handleDialogOpen(restoreActionOpen, item);

    const deleteAction = (reason: string) => {
        deleteMutation({
            variables: {
                actionId: currentAction.id,
                observationId: currentAction.observationId,
                reason,
            },
            refetchQueries: [
                {
                    query: refetchQuery,
                    variables: { studyId },
                },
            ],
        })
            .then(() => {
                if (onUpdate) {
                    onUpdate();
                }
                handleDialogClose(deleteActionOpen);
                NotificationService.showNotification(t('cm:deleteActionSuccessMessage'));
            })
            .catch(() => NotificationService.showNotification(t('errorMessage')));
    };

    const restoreAction = () => {
        restoreMutation({
            variables: {
                actionId: currentAction.id,
                observationId: currentAction.observationId,
            },
            refetchQueries: [
                {
                    query: refetchQuery,
                    variables: { studyId },
                },
            ],
        })
            .then(() => {
                if (onUpdate) {
                    onUpdate();
                }
                handleDialogClose(restoreActionOpen);
                NotificationService.showNotification(t('cm:restoreActionSuccessMessage'));
            })
            .catch(() => NotificationService.showNotification(t('errorMessage')));
    };

    const dialogTitle = t(currentAction?.deleted ? 'cm:restoreActionDialogHeading' : 'cm:deleteActionDialogHeading', {
        itemId: currentAction?.recordId,
    });
    const restoreMessage = t(
        currentAction?._obsDeleted ? 'cm:restoreActionDialogDeletedObsMessage' : 'cm:restoreActionDialogMessage',
    );

    return (
        <>
            {deleteActionOpen.value && (
                <DeleteReasonDialog
                    title={dialogTitle}
                    handleClose={() => handleDialogClose(deleteActionOpen)}
                    handleSubmit={deleteAction}
                />
            )}
            {restoreActionOpen.value && (
                <ActionDialog
                    title={dialogTitle}
                    onClose={() => handleDialogClose(restoreActionOpen)}
                    onSubmit={restoreAction}
                    submitText={t('restore')}
                    asNotification={currentAction._obsDeleted}
                    className="action-dialog-small"
                >
                    <div>{restoreMessage}</div>
                </ActionDialog>
            )}
            {children(handleDeleteAct, handleRestoreAct)}
        </>
    );
};

export default ControlAction;
